import React, { useEffect, useState, useMemo, useCallback } from "react";
import CompanyInfo from "./CompanyInfo";
import "./company.css";
import CompanyAddress from "./CompanyAddress";
import { StepperImg } from "../../assets/image";
import Accounting from "./Accounting";
import Payment from "./Payment";
import PaymentLast from "./PaymentLast";
import { CheckCircle } from "../../assets/svg/AllSvg";
import { Provider } from "./CompanyContext";
import { useLocation } from "react-router-dom";
import DirectorsShareholders from "./directorsShareholders/DirectorsShareholders";
import { getOrganisationDetails } from "../../redux/organisation/slice";
import { useDispatch, useSelector } from "react-redux";
import { companyData } from "../../redux/companies/slice";

const NewCompany = () => {
  const [currentStep, setCurrentStep] = useState(0); 
  const [companyId, setCompanyId] = useState(undefined);

  const location = useLocation();
  const dispatch = useDispatch();
  const type = location?.state?.companyType;

  // Selector
  const { organisationDetails, loading, CompanyData } = useSelector(
    (store) => ({
      organisationDetails:
        store?.organisationData?.getOrganisationDetailsStatus?.data?.data,
      loading: store?.organisationData?.loading,
      // Company Data
      CompanyData: store?.companiesData?.companyDataStatus?.data?.data,
    })
  );

  // Get APIs
  useEffect(() => {
    if (companyId) {
      dispatch(getOrganisationDetails(companyId));
    }
  }, [companyId]);

  useEffect(() => {
    dispatch(companyData());
  }, []);

  // Memoize tab labels to prevent recreation on every render
  const tabLabel = useMemo(
    () => [
      { value: "company_information", label: "Company Information" },
      { value: "company_address", label: "Company Address" },
      { value: "directors_shareholders", label: "Directors & Shareholders" },
      { value: "employment_support", label: "Employment Support" },
      { value: "payment", label: "Payment" },
    ],
    []
  );

  // Memoize step click handler to prevent recreation
  const handleStepClick = useCallback((step) => {
    setCurrentStep(step);
  }, []);

  // Memoize step component props to prevent unnecessary re-renders
  const stepProps = useMemo(
    () => ({
      companyId,
      setCompanyId,
      loading,
      organisationDetails: companyId ? organisationDetails : null,
      CompanyData,
      type,
      handleStepClick,
    }),
    [
      companyId,
      loading,
      organisationDetails,
      CompanyData,
      type,
      handleStepClick,
    ]
  );

  // Memoize step rendering to prevent recreation on every render
  const renderStep = useCallback(
    (step) => {
      const currentStepValue = tabLabel[step]?.value;

      switch (currentStepValue) {
        case "company_information":
          return <CompanyInfo {...stepProps} />;
        case "company_address":
          return (
            <CompanyAddress
              {...stepProps}
              addresses={companyId ? organisationDetails?.addresses ?? [] : []}
              business_registered_address_fee={
                CompanyData?.other?.business_registered_address_fee ?? "-"
              }
            />
          );
        case "directors_shareholders":
          return (
            <DirectorsShareholders
              companyId={companyId}
              shareholders={
                companyId ? organisationDetails?.shareholders ?? [] : []
              }
              resident_directors={
                companyId ? organisationDetails?.resident_directors ?? [] : []
              }
              nominee_directors={
                companyId ? organisationDetails?.nominee_directors ?? [] : []
              }
              nominee_shareholders={
                companyId ? organisationDetails?.nominee_shareholders ?? [] : []
              }
              nominee_director_deposits={
                companyId
                  ? organisationDetails?.nominee_director_deposits ?? ""
                  : ""
              }
              nominee_director_durations={
                companyId
                  ? organisationDetails?.nominee_director_durations ?? ""
                  : ""
              }
              nominee_shareholder_durations={
                companyId
                  ? organisationDetails?.nominee_shareholder_durations ?? ""
                  : ""
              }
              nominee_shareholder_deposits={
                companyId
                  ? organisationDetails?.nominee_shareholder_deposits ?? ""
                  : ""
              }
              nominee_director_shareholder_durations={
                companyId
                  ? organisationDetails?.nominee_director_shareholder_durations ??
                    ""
                  : ""
              }
              nominee_director_shareholder_deposits={
                companyId
                  ? organisationDetails?.nominee_director_shareholder_deposits ??
                    ""
                  : ""
              }
              loading={loading}
              use_nominee_director={
                companyId ? organisationDetails?.use_nominee_director : false
              }
              use_nominee_shareholder={
                companyId ? organisationDetails?.use_nominee_shareholder : false
              }
              total_number_of_shares={
                companyId
                  ? organisationDetails?.total_number_of_shares ?? ""
                  : ""
              }
              total_issued_share_capital={
                companyId
                  ? organisationDetails?.total_issued_share_capital ?? ""
                  : ""
              }
              total_paid_up_share_capital={
                companyId
                  ? organisationDetails?.total_paid_up_share_capital ?? ""
                  : ""
              }
              currency={companyId ? organisationDetails?.currency ?? "" : ""}
            />
          );
        case "employment_support":
          return (
            <Accounting
              {...stepProps}
              no_of_eps={companyId ? organisationDetails?.no_of_eps ?? "" : ""}
              no_of_locs={
                companyId ? organisationDetails?.no_of_locs ?? "" : ""
              }
            />
          );
        case "payment":
          return (
            <Payment
              {...stepProps}
              no_of_eps={companyId ? organisationDetails?.no_of_eps ?? "" : ""}
              no_of_locs={
                companyId ? organisationDetails?.no_of_locs ?? "" : ""
              }
            />
          );
        case "confirmation":
          return <PaymentLast {...stepProps} />;
        default:
          return null;
      }
    },
    [tabLabel, stepProps, companyId, organisationDetails, CompanyData, loading]
  );

  // Memoize navigation functions to prevent recreation
  const next = useCallback(() => {
    if (currentStep < tabLabel.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep, tabLabel.length]);

  const prev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  // Memoize stepper items to prevent recreation on every render
  const stepperItems = useMemo(
    () =>
      tabLabel?.map((step, index) => (
        <div
          key={index}
          className="flex items-center space-x-3 sm:text-left text-center"
        >
          <span
            className={`xl:max-w-8 max-w-6 xl:min-w-8 min-w-6 w-full xl:h-8 h-6 rounded-full flex justify-center items-center bg-gray-400 text-white xl:text-base sm:text-sm text-xs ${
              index < currentStep ? "!bg-white text-green-700" : ""
            }${index === currentStep ? "!bg-theme" : ""}`}
          >
            {index < currentStep ? (
              <CheckCircle
                className="xl:w-8 sm:w-6 w-5 xl:h-8 sm:h-6 h-6"
                pathClass={"fill-green-700"}
              />
            ) : (
              index + 1
            )}
          </span>
          <p
            className={`xl:text-base text-sm ${
              index < currentStep ? "text-green-700" : ""
            } ${index === currentStep ? "font-semibold" : ""}`}
          >
            {step.label}
          </p>
        </div>
      )),
    [tabLabel, currentStep]
  );

  return (
    <div className="sm:flex">
      {/* stepper */}
      <Provider value={{ next, prev }}>
        <div className="p-4 sm:mb-0 mb-5">
          <img
            className="w-30 h-30 relative bottom-1 right-4"
            src={StepperImg}
            alt="Stepper"
          />
          <div className="stepper xl:space-y-10 space-y-8">{stepperItems}</div>
        </div>
        {/* stepper */}
        <div className="w-full">{renderStep(currentStep)}</div>
      </Provider>
    </div>
  );
};

export default NewCompany;
